import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { match } from 'ts-pattern';
import { Option } from '@sapphire/result';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { CURRENCY_CODE, markCached, Nullable } from '@ps/utils';
import {
  AuthSelectorsFacade,
  AuthSessionActions,
  ImpersonationActions,
  isBuyerUser,
} from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';

import { SettingsService } from '../services/settings.service';
import { EntitlementService } from '../services/entitlement.service';
import { UserSettingsStorageService } from '../services/user-settings-storage.service';
import { DashboardService } from '../services/dashboard.service';
import { ReportPreferenceService } from '../services/report-preference.service';
import { CurrencySettings } from '../models/currency-settings.model';
import {
  UserSettingsActions,
  DashboardActions,
  DashboardApiActions,
  ReportPreferenceActions,
  ReportPreferenceApiActions,
  BillingRulesActions,
  BillingRulesApiActions,
} from './user-settings.actions';
import {
  selectAllBuyerSettings,
  selectBillingRulesCachedAt,
  selectCurrencySettings,
  selectPreferencesCachedAt,
  selectReportPreferencesCachedAt,
} from './user-settings.selectors';
import {
  BILLING_RULES_CACHE_TTL,
  DASHBOARD_CACHE_TTL,
  REPORT_PREFERENCE_CACHE_TTL,
} from './user-settings.constants';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class UserSettingsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _settingsService = inject(SettingsService);
  private readonly _entitlementService = inject(EntitlementService);
  private readonly _storage = inject(UserSettingsStorageService);
  private readonly _auth = inject(AuthSelectorsFacade);
  private readonly _dashboardService = inject(DashboardService);
  private readonly _reportPreferenceService = inject(ReportPreferenceService);
  private readonly _toast = inject(ToastService);

  public loadOnLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.loginSuccess),
      map(({ user }) => UserSettingsActions.load({ user })),
    ),
  );

  // On boot, auth restores the session via `rehydrate`. We then restore the
  // persisted user settings from storage. If they can't be restored, the
  // session is incomplete/corrupt — clear storage and send the user back to
  // login (`clearState` resets the store, clears storage, navigates to /login).
  public rehydrateOnAuthRestore$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.rehydrate),
      map(() =>
        this._storage.load().match({
          some: (persisted) => UserSettingsActions.rehydrate(persisted),
          none: () => AuthSessionActions.clearState(),
        }),
      ),
    ),
  );

  public load$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserSettingsActions.load),
      switchMap(({ user }) =>
        forkJoin({
          allBuyerSettings: isBuyerUser(user)
            ? this._settingsService.getAllBuyerSettings(user)
            : of({}),
          companySettingsOption: this._settingsService.getCompanySettings(
            user.companyId,
          ),
          entitlements: this._entitlementService.getEntitlements(user),
        }).pipe(
          switchMap(
            ({ allBuyerSettings, companySettingsOption, entitlements }) => {
              const companySettings = companySettingsOption.unwrapOr(null);
              const fx = companySettings?.fx ?? CURRENCY_CODE.US;
              return this._settingsService.getCurrencySettings(fx).pipe(
                map((currencySettings) =>
                  UserSettingsActions.loadSuccess({
                    allBuyerSettings,
                    companySettings,
                    currencySettings,
                    entitlements,
                  }),
                ),
              );
            },
          ),
        ),
      ),
      this.psCatchError(() => of(AuthSessionActions.logout()), {
        notify: true,
        customMessage: 'Unable to proceed right now. Please try again later.',
      }),
    ),
  );

  public persist$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserSettingsActions.loadSuccess),
        tap(
          ({
            allBuyerSettings,
            companySettings,
            currencySettings,
            entitlements,
          }) =>
            this._storage.save(
              {
                allBuyerSettings,
                companySettings: companySettings ?? {},
                currencySettings,
                entitlements,
              },
              this._auth.rememberMe(),
            ),
        ),
      ),
    { dispatch: false },
  );

  public loadPreferences$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DashboardActions.loadPreferences),
      withCacheGate(
        this._store.select(selectPreferencesCachedAt),
        DASHBOARD_CACHE_TTL,
      ),
      switchMap(({ params }) =>
        this._dashboardService.getPreferences(params).pipe(
          map((preferences) =>
            DashboardApiActions.loadPreferencesSuccess({
              preferences,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              DashboardApiActions.loadPreferencesFailure({
                error: err.message || 'Failed to load dashboard preferences',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updatePreferences$ = createEffect(() =>
    this._actions$.pipe(
      ofType(DashboardApiActions.updatePreferences),
      switchMap(({ preferences }) =>
        this._dashboardService.updatePreferences(preferences).pipe(
          map(() => {
            this._toast.success('Dashboard preferences saved with success!', {
              keepOpen: false,
            });
            return DashboardApiActions.updateSuccess();
          }),
          this.psCatchError(
            (err) =>
              of(
                DashboardApiActions.updateFailure({
                  error:
                    err.message || 'Failed to update dashboard preferences',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadReportPreference$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportPreferenceActions.loadReportPreference),
      withCacheGate(
        this._store.select(selectReportPreferencesCachedAt),
        REPORT_PREFERENCE_CACHE_TTL,
      ),
      switchMap(({ userId }) =>
        this._reportPreferenceService.getReportPreference(userId).pipe(
          map((preferences) =>
            ReportPreferenceApiActions.loadReportPreferenceSuccess({
              preferences,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              ReportPreferenceApiActions.loadReportPreferenceFailure({
                error: err.message || 'Failed to load report preferences',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public saveReportPreference$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ReportPreferenceActions.saveReportPreference),
      switchMap(({ userId, preferences }) =>
        this._reportPreferenceService
          .saveReportPreference(userId, preferences)
          .pipe(
            map(() => {
              this._toast.success('Preferences Saved sucessfully!!', {
                keepOpen: false,
              });
              return ReportPreferenceApiActions.saveReportPreferenceSuccess();
            }),
            this.psCatchError((err) =>
              of(
                ReportPreferenceApiActions.saveReportPreferenceFailure({
                  error: err.message || 'Failed to save report preferences',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public loadBillingRules$ = createEffect(() =>
    this._actions$.pipe(
      ofType(BillingRulesActions.loadBillingRules),
      withCacheGate(
        this._store.select(selectBillingRulesCachedAt),
        BILLING_RULES_CACHE_TTL,
      ),
      concatLatestFrom(() => this._store.select(selectAllBuyerSettings)),
      switchMap(([{ selectedBuyerId }, allBuyerConfigs]) =>
        this._settingsService
          .getBillingRules(
            this._auth.user().unwrap(),
            allBuyerConfigs,
            selectedBuyerId,
          )
          .pipe(
            map((record) =>
              BillingRulesApiActions.loadBillingRulesSuccess({
                record,
                cachedAt: record ? markCached() : null,
              }),
            ),
            this.psCatchError(() =>
              of(BillingRulesApiActions.loadBillingRulesFailure()),
            ),
          ),
      ),
    ),
  );

  // ---------------------------------------------------------------------------
  // Impersonation — reload company settings, currency, and dashboard prefs
  // for the active context (operator's own, or the impersonated buyer's)
  // ---------------------------------------------------------------------------

  public onImpersonationChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        ImpersonationActions.connectSuccess,
        ImpersonationActions.connectFailure,
        ImpersonationActions.disconnectSuccess,
        ImpersonationActions.rehydrate,
      ),
      map((action) =>
        match(action)
          .with(
            { type: ImpersonationActions.connectSuccess.type },
            { type: ImpersonationActions.rehydrate.type },
            ({ buyerCompany }) => ({
              isConnected: true,
              companyId: buyerCompany.id,
            }),
          )
          .otherwise(() => ({
            isConnected: false,
            companyId: this._auth.user().unwrap().companyId,
          })),
      ),
      switchMap(({ isConnected, companyId }) =>
        of(
          DashboardActions.invalidateCache(),
          DashboardActions.loadPreferences({
            params: { isServiceBuyer: isConnected },
          }),
          UserSettingsActions.loadServiceOperatorSettings({
            companyId,
            hasImpersonatedBuyer: isConnected,
          }),
        ),
      ),
    ),
  );

  public loadServiceOperatorSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserSettingsActions.loadServiceOperatorSettings),
      concatLatestFrom(() => this._store.select(selectCurrencySettings)),
      switchMap(
        ([{ companyId, hasImpersonatedBuyer }, previousCurrencySettings]) =>
          this._settingsService.getCompanySettings(companyId).pipe(
            switchMap((companySettings) => {
              const fx = companySettings
                .mapInto((settings) => Option.from(settings.fx))
                .unwrapOr(CURRENCY_CODE.US);

              return this._resolveCurrencySettings(
                fx,
                previousCurrencySettings,
              ).pipe(
                map((currencySettings) =>
                  UserSettingsActions.loadServiceOperatorSettingsSuccess({
                    companySettings: companySettings.unwrapOr(null),
                    currencySettings,
                    companyId,
                    hasImpersonatedBuyer,
                  }),
                ),
              );
            }),
            this.psCatchError(
              (err) =>
                of(
                  UserSettingsActions.loadServiceOperatorSettingsFailure({
                    error:
                      err.message || 'Failed to load impersonation settings',
                    companyId,
                    hasImpersonatedBuyer,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  /**
   * Only reset buyer-scoped domain state once the new context
   * has actually finished loading — on success or failure —
   * so screens never flash empty while mid-reload.
   */
  public impersonationContextSettled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        UserSettingsActions.loadServiceOperatorSettingsSuccess,
        UserSettingsActions.loadServiceOperatorSettingsFailure,
      ),
      map(({ companyId, hasImpersonatedBuyer }) =>
        ImpersonationActions.contextSettled({
          companyId,
          hasImpersonatedBuyer,
        }),
      ),
    ),
  );

  /** Reuses the cached currency settings when `fx` hasn't changed, instead of re-fetching. */
  private _resolveCurrencySettings(
    fx: number,
    previous: Nullable<CurrencySettings>,
  ): Observable<CurrencySettings> {
    return previous?.fx === fx
      ? of(previous)
      : this._settingsService.getCurrencySettings(fx);
  }
}
